"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleAlert as AlertCircle, ArrowRight, Clock, Droplet, Flame, PhoneCall, Pipette, Search, ShieldCheck, Sparkles, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface ServiceItem {
  title: string;
  description: string;
  iconName: string;
  link: string;
  badge: string;
  urgency: string;
}
const servicesData: ServiceItem[] = [{
  title: "Emergency Plumbing",
  description: "Rapid dispatch 24/7 for burst pipes, major flooding, and urgent water shutoffs across Jonesboro.",
  iconName: "emergency",
  link: "/emergency-plumbing/",
  badge: "24/7 Urgent",
  urgency: "Immediate Dispatch"
}, {
  title: "Drain Cleaning",
  description: "Complete blockage clearing for sinks, showers, and main floor drains using motorized snaking and hydro-jetting.",
  iconName: "drain",
  link: "/drain-cleaning/",
  badge: "Same-Day",
  urgency: "Fast Clearing"
}, {
  title: "Water Heater Repair",
  description: "Diagnostic and repair service for tankless, gas, and electric water heaters with no hot water or leaking tanks.",
  iconName: "heater",
  link: "/water-heater-repair/",
  badge: "Top Rated",
  urgency: "All Major Brands"
}, {
  title: "Leak Detection",
  description: "Non-invasive electronic leak detection for hidden wall pipes, underground supply lines, and foundation slabs.",
  iconName: "leak",
  link: "/leak-detection-repair/",
  badge: "Non-Invasive",
  urgency: "Pinpoint Precision"
}, {
  title: "Toilet Repair",
  description: "Fast repairs for clogged bowls, constantly running tanks, leaking bases, and full toilet replacements.",
  iconName: "toilet",
  link: "/toilet-repair/",
  badge: "Fast Fix",
  urgency: "Zero Mess Guarantee"
}, {
  title: "Sewer Line Service",
  description: "Camera video inspections, root intrusion clearing, and trenchless line repairs for backed-up sewer pipes.",
  iconName: "sewer",
  link: "/sewer-line-repair/",
  badge: "Video Inspection",
  urgency: "Root & Line Repair"
}, {
  title: "Pipe Repair",
  description: "Fixes for frozen burst pipes, corroded copper, polybutylene replacements, and noisy hammering water lines.",
  iconName: "pipe",
  link: "/pipe-repair/",
  badge: "Durable Fix",
  urgency: "Copper & PEX"
}, {
  title: "Faucet Repair",
  description: "Fixing dripping faucets, low water pressure, worn cartridges, and new fixture installations for kitchens and baths.",
  iconName: "faucet",
  link: "/faucet-sink-repair/",
  badge: "Precision Care",
  urgency: "Stop Water Waste"
}];
function getServiceIcon(name: string) {
  switch (name) {
    case "emergency":
      return <AlertCircle className="size-6 text-destructive" />;
    case "drain":
      return <Droplet className="size-6 text-primary" />;
    case "heater":
      return <Flame className="size-6 text-accent-foreground" />;
    case "leak":
      return <Search className="size-6 text-primary" />;
    case "toilet":
      return <Wrench className="size-6 text-primary" />;
    case "sewer":
      return <Pipette className="size-6 text-primary" />;
    case "pipe":
      return <ShieldCheck className="size-6 text-primary" />;
    case "faucet":
      return <Sparkles className="size-6 text-primary" />;
    default:
      return <Wrench className="size-6 text-primary" />;
  }
}
export default function ServicesGrid() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicesgrid" data-nav="light" className="w-full bg-background py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-2xl">
            <Badge variant="secondary" className="mb-3 px-3 py-1 font-semibold text-xs tracking-wide">
              Jonesboro 24/7 Plumbing Services
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
              Comprehensive Plumbing Solutions
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground">
              Direct emergency assistance and scheduled plumbing maintenance across Jonesboro and surrounding areas.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Button asChild size="lg" className="shadow-sm font-semibold">
              <a href="tel:+14708479076" className="inline-flex items-center justify-center gap-2">
                <PhoneCall className="size-4" />
                <span>Call (470) 847-9076</span>
              </a>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/services/" className="inline-flex items-center justify-center gap-2">
                <span>View All Services</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {servicesData.map((service, index) => <motion.div key={service.title} variants={itemVariants} data-index={index}>
              <Card className="h-full border border-border shadow-xs hover:shadow-md transition-[box-shadow,border-color] hover:border-primary/40 flex flex-col justify-between group">
                <div>
                  <CardHeader className="space-y-3 pb-3">
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-lg bg-secondary/80 text-foreground group-hover:bg-primary/10 transition-colors">
                        {getServiceIcon(service.iconName)}
                      </div>
                      <Badge variant="outline" className="text-xs bg-card">
                        {service.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold font-default text-foreground group-hover:text-primary transition-colors">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {service.description}
                    </CardDescription>
                    <div className="mt-4 flex items-center gap-1.5 text-xs font-medium text-foreground/80">
                      <Clock className="size-3.5 text-muted-foreground" />
                      <span>{service.urgency}</span>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-4 border-t border-border/60">
                  <Button asChild variant="ghost" size="sm" className="w-full justify-between px-2 text-foreground font-semibold group-hover:text-primary">
                    <Link to={service.link} className="flex items-center w-full justify-between">
                      <span>Service Details</span>
                      <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-12 rounded-xl bg-secondary/60 border border-border p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-primary text-primary-foreground hidden sm:flex items-center justify-center">
              <Clock className="size-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold font-default text-foreground">
                Experiencing an active water or sewage leak?
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Our master plumbers are ready with immediate emergency response throughout Clayton County.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <Button asChild size="lg" className="w-full md:w-auto shadow-sm">
              <a href="tel:+14708479076" className="inline-flex items-center justify-center gap-2 font-bold">
                <PhoneCall className="size-4" />
                <span>Call Plumber Now</span>
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}